/**
 * Directory lookup client — the only place the SPA talks to Microsoft Graph
 * (indirectly, through our own Function App).
 *
 * This is reference data, not ticket data. It reads Entra and stores nothing,
 * exactly like `/api/access/me`, so it does not touch the "no database until the
 * walkthroughs land" gate.
 *
 * EVERY failure path returns an empty answer instead of throwing. A directory
 * that is slow, unreachable, or not yet permissioned must degrade the field to
 * an ordinary text box — never block someone from raising a ticket. The one
 * thing worse than typing a name by hand is not being able to.
 */

const MIN_QUERY = 2;

/** → GET /api/people?q= */
export async function searchPeople(query, { signal } = {}) {
  const q = String(query ?? '').trim();
  if (q.length < MIN_QUERY) return { people: [], available: true };

  try {
    const res = await fetch(`/api/people?q=${encodeURIComponent(q)}`, {
      signal,
      headers: { Accept: 'application/json' },
    });
    // 401 means the session lapsed; 404 means no backend at all (a local
    // `vite preview`, which is how the e2e suite runs). Both mean "just let
    // them type", not "show an error".
    if (!res.ok) return { people: [], available: false };

    const body = await res.json();
    return {
      people: Array.isArray(body?.people) ? body.people : [],
      // The API says `directory-unavailable` while the Function App's managed
      // identity is still waiting on its User.Read.All grant.
      available: body?.reason !== 'directory-unavailable',
    };
  } catch {
    // Includes AbortError from a superseded keystroke — the caller ignores the
    // result in that case anyway.
    return { people: [], available: false };
  }
}

/** → GET /api/people/{id}/manager. `null` is the ordinary answer, not a failure. */
export async function managerOf(id, { signal } = {}) {
  if (!id) return null;
  try {
    const res = await fetch(`/api/people/${encodeURIComponent(id)}/manager`, {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.manager ?? null;
  } catch {
    return null;
  }
}

/**
 * The people the signed-in person may raise a request for: everyone below them in Paylocity.
 * `[]` for anyone with nobody below them, AND whenever the list cannot be had (not turned on
 * yet, offline, the walkthrough build). The "who is this for?" choice is simply not offered then.
 */
export async function myTeam({ signal } = {}) {
  try {
    const res = await fetch('/api/people/my-team', { signal, headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body?.people)
      ? body.people.filter((p) => p && typeof p.upn === 'string' && p.upn).map((p) => ({ upn: p.upn, name: typeof p.name === 'string' && p.name ? p.name : p.upn }))
      : [];
  } catch {
    return [];
  }
}

/** The display string for a value that may be a picked person or typed text. */
export function personLabel(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.displayName || value.mail || '';
}
